import React, { useState, useMemo, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { SlidersHorizontal, X, ChevronDown } from 'lucide-react';
import ProductCard from '../components/ProductCard';
import { useProducts } from '../hooks/useProducts';

const CONCERNS = ['Dry Skin', 'Oily Skin', 'Acne', 'Acne Scars', 'Dark Spots', 'Uneven Tone', 'Fine Lines'];
const SIZES = ['10ml', '30ml'];
const SORT_OPTIONS = [
  { value: 'popular', label: 'Most Popular' },
  { value: 'price-asc', label: 'Price: Low to High' },
  { value: 'price-desc', label: 'Price: High to Low' },
  { value: 'rating', label: 'Highest Rated' },
  { value: 'new', label: 'Newest' },
];

export default function Shop() {
  const [searchParams] = useSearchParams();
  const searchQueryParam = searchParams.get('search') ? searchParams.get('search').trim() : '';
  const { products, loading } = useProducts();
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [selectedConcerns, setSelectedConcerns] = useState(() => {
    const c = searchParams.get('concern');
    return c ? [c] : [];
  });
  const [selectedSizes, setSelectedSizes] = useState([]);
  const [maxPrice, setMaxPrice] = useState(2500);
  const [sort, setSort] = useState('popular');
  const [sortOpen, setSortOpen] = useState(false);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const toggleFilter = (arr, setArr, val) => {
    setArr(prev => prev.includes(val) ? prev.filter(x => x !== val) : [...prev, val]);
  };

  const clearFilters = () => {
    setSelectedConcerns([]);
    setSelectedSizes([]);
    setMaxPrice(2500);
  };

  const filtered = useMemo(() => {
    let list = [...products];
    const searchLower = searchQueryParam && searchQueryParam.toLowerCase();
    if (searchLower) {
      list = list.filter(p => (
        (p.name && p.name.toLowerCase().includes(searchLower)) ||
        (p.shortName && p.shortName.toLowerCase().includes(searchLower))
      ));
    }
    if (selectedConcerns.length > 0) {
      list = list.filter(p => p.concern.some(c => selectedConcerns.includes(c)));
    }
    if (selectedSizes.length > 0) {
      list = list.filter(p => selectedSizes.includes(p.size));
    }
    list = list.filter(p => p.price <= maxPrice);
    switch (sort) {
      case 'price-asc': list.sort((a, b) => a.price - b.price); break;
      case 'price-desc': list.sort((a, b) => b.price - a.price); break;
      case 'rating': list.sort((a, b) => b.rating - a.rating); break;
      default: list.sort((a, b) => b.reviews - a.reviews);
    }
    return list;
  }, [products, searchQueryParam, selectedConcerns, selectedSizes, maxPrice, sort]);

  const hasFilters = selectedConcerns.length > 0 || selectedSizes.length > 0 || maxPrice < 2500;

  return (
    <main className="min-h-screen">
      {/* Header */}
      <section className="bg-[#fdf8f6] py-16 px-6 lg:px-8 text-center">
        <p className="text-xs tracking-[0.3em] uppercase text-gray-400 mb-2">Collection</p>
        <h1 className="font-display text-5xl sm:text-6xl font-light">Shop All</h1>
        <p className="mt-4 text-gray-500 text-sm font-light">
          {filtered.length} product{filtered.length !== 1 ? 's' : ''}
        </p>
      </section>

      <div className="max-w-7xl mx-auto px-6 lg:px-8 py-10">
        {/* Loading state */}
        {loading && (
          <div className="flex items-center justify-center py-24">
            <p className="text-gray-400 text-sm font-light">Loading products...</p>
          </div>
        )}

        {!loading && (
          <>
        {/* Controls bar */}
        <div className="flex items-center justify-between mb-8">
          <button
            onClick={() => setFiltersOpen(!filtersOpen)}
            className="flex items-center gap-2 text-xs tracking-[0.15em] uppercase font-medium hover:text-gray-500 transition-colors"
          >
            <SlidersHorizontal size={15} />
            Filter {hasFilters && <span className="bg-black text-white text-[9px] px-1.5 py-0.5 rounded-full">{selectedConcerns.length + selectedSizes.length}</span>}
          </button>

          {/* Sort dropdown */}
          <div className="relative">
            <button
              onClick={() => setSortOpen(!sortOpen)}
              className="flex items-center gap-2 text-xs tracking-[0.15em] uppercase font-medium hover:text-gray-500 transition-colors"
            >
              Sort: {SORT_OPTIONS.find(o => o.value === sort)?.label}
              <ChevronDown size={13} className={`transition-transform ${sortOpen ? 'rotate-180' : ''}`} />
            </button>
            {sortOpen && (
              <div className="absolute right-0 top-full mt-2 bg-white border border-gray-200 shadow-lg z-20 w-48 py-1">
                {SORT_OPTIONS.map(o => (
                  <button
                    key={o.value}
                    onClick={() => { setSort(o.value); setSortOpen(false); }}
                    className={`w-full text-left px-4 py-2.5 text-xs tracking-wide hover:bg-gray-50 transition-colors ${sort === o.value ? 'font-medium' : 'text-gray-500'}`}
                  >
                    {o.label}
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>

        <div className="flex gap-10">
          {/* Sidebar filters */}
          <aside className={`flex-shrink-0 transition-all duration-300 overflow-hidden ${filtersOpen ? 'w-60 opacity-100' : 'w-0 opacity-0'}`}>
            <div className="w-60">
              <div className="flex items-center justify-between mb-6">
                <p className="text-xs tracking-[0.2em] uppercase font-medium">Filters</p>
                {hasFilters && (
                  <button onClick={clearFilters} className="text-xs text-gray-400 hover:text-black transition-colors flex items-center gap-1">
                    <X size={11} /> Clear
                  </button>
                )}
              </div>

              {/* Skin Concern */}
              <div className="mb-8">
                <p className="text-xs tracking-[0.15em] uppercase text-gray-400 mb-3">Skin Concern</p>
                <div className="flex flex-col gap-2">
                  {CONCERNS.map(c => (
                    <label key={c} className="flex items-center gap-2.5 cursor-pointer group">
                      <input
                        type="checkbox"
                        checked={selectedConcerns.includes(c)}
                        onChange={() => toggleFilter(selectedConcerns, setSelectedConcerns, c)}
                        className="w-3.5 h-3.5 accent-black cursor-pointer"
                      />
                      <span className="text-sm text-gray-600 group-hover:text-black transition-colors">{c}</span>
                    </label>
                  ))}
                </div>
              </div>

              {/* Size */}
              <div className="mb-8">
                <p className="text-xs tracking-[0.15em] uppercase text-gray-400 mb-3">Size</p>
                <div className="flex gap-2">
                  {SIZES.map(s => (
                    <button
                      key={s}
                      onClick={() => toggleFilter(selectedSizes, setSelectedSizes, s)}
                      className={`px-3 py-1.5 text-xs border transition-colors ${selectedSizes.includes(s) ? 'bg-black text-white border-black' : 'border-gray-200 hover:border-black'}`}
                    >
                      {s}
                    </button>
                  ))}
                </div>
              </div>

              {/* Price */}
              <div className="mb-8">
                <p className="text-xs tracking-[0.15em] uppercase text-gray-400 mb-3">Max Price: ₹{maxPrice.toLocaleString()}</p>
                <input
                  type="range"
                  min={499}
                  max={2500}
                  step={100}
                  value={maxPrice}
                  onChange={e => setMaxPrice(Number(e.target.value))}
                  className="w-full accent-black"
                />
                <div className="flex justify-between text-xs text-gray-400 mt-1">
                  <span>₹499</span>
                  <span>₹2,500</span>
                </div>
              </div>
            </div>
          </aside>

          {/* Product grid */}
          <div className="flex-1 min-w-0">
            {/* Active filter chips */}
            {hasFilters && (
              <div className="flex flex-wrap gap-2 mb-6">
                {selectedConcerns.map(c => (
                  <button
                    key={c}
                    onClick={() => toggleFilter(selectedConcerns, setSelectedConcerns, c)}
                    className="flex items-center gap-1.5 text-xs bg-black text-white px-3 py-1.5 rounded-full"
                  >
                    {c} <X size={10} />
                  </button>
                ))}
                {selectedSizes.map(s => (
                  <button
                    key={s}
                    onClick={() => toggleFilter(selectedSizes, setSelectedSizes, s)}
                    className="flex items-center gap-1.5 text-xs bg-black text-white px-3 py-1.5 rounded-full"
                  >
                    {s} <X size={10} />
                  </button>
                ))}
              </div>
            )}

            {filtered.length === 0 ? (
              searchQueryParam ? (
                <div className="py-6">
                  <div className="text-center py-12">
                    <p className="font-display text-2xl font-light text-gray-700 mb-3">We couldn't find any matches!</p>
                    <p className="text-sm text-gray-500 mb-6">Showing all products instead.</p>
                  </div>
                  <div className="grid grid-cols-2 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                    {products.map(p => <ProductCard key={p.id} product={p} />)}
                  </div>
                </div>
              ) : (
                <div className="text-center py-24">
                  <p className="font-display text-2xl font-light text-gray-400 mb-3">No products found</p>
                  <button onClick={clearFilters} className="text-xs tracking-widest uppercase border-b border-black">
                    Clear filters
                  </button>
                </div>
              )
            ) : (
              <div className="grid grid-cols-2 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                {filtered.map(p => <ProductCard key={p.id} product={p} />)}
              </div>
            )}
          </div>
        </div>
        </>
        )}
      </div>
    </main>
  );
}
